import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { tokenService } from "../../services/api";
import { copilotApi } from "./useApi";
import type { QuickFactsData } from "../types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "quick_facts";
  content: string;
  isStreaming?: boolean;
  timestamp: number;
  /** Origin of this message: 'text' (typed) or 'voice' (spoken) */
  source?: "text" | "voice";
  /** Whether the AI response was spoken aloud via TTS */
  spokenAloud?: boolean;
  /** Quick facts data for role === "quick_facts" */
  quickFactsData?: QuickFactsData;
  /** Query log ID for feedback (set on assistant messages after streaming) */
  logId?: string;
  /** User feedback state: 1=up, -1=down, 0=neutral */
  feedbackState?: number;
  /** Tool calls made during this response (for displaying indicators) */
  toolCalls?: Array<{
    tool_name: string;
    status: string;
    duration_ms?: number;
  }>;
}

export interface UseSSEChatOptions {
  /** Account UUID to scope the conversation */
  accountUuid?: string;
  /** Called when a full assistant response has finished */
  onResponseComplete?: (fullText: string) => void;
  /** Pre-loaded messages from sessionStorage (avoids server round-trip) */
  initialMessages?: ChatMessage[];
  /** Pre-loaded conversation ID from sessionStorage */
  initialConversationId?: string | null;
}

export interface UseSSEChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  conversationId: string | null;
  error: string | null;
  /** Whether conversation history is being loaded from the server */
  isLoadingHistory: boolean;
  /** Send a message. Optional source for voice vs text origin. */
  sendMessage: (text: string, source?: "text" | "voice") => void;
  clearConversation: () => void;
  /** Mark the latest assistant message as spoken aloud */
  markLastSpoken: () => void;
  /** Load a conversation from the server by ID, restoring messages */
  loadConversation: (conversationId: string) => Promise<void>;
  /** Record that feedback was submitted for a message */
  setMessageFeedback: (messageId: string, feedback: number) => void;
}

/** localStorage key for persisting the active conversation ID */
const CONVERSATION_ID_KEY = "cm_copilot_conversation_id";

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL || "/api";

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 */
async function tryRefreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await axios.post<{ access_token: string; expires_in: number }>(
      `${API_BASE}/auth/refresh`,
      { refresh_token: refreshToken },
    );
    const { access_token, expires_in } = res.data;
    localStorage.setItem("cm_access_token", access_token);
    document.cookie = `cm_auth=${access_token}; path=/; max-age=${expires_in}; SameSite=Lax`;
    return access_token;
  } catch {
    return null;
  }
}

/**
 * Make a POST fetch request with auth, handling 401 with a single
 * token-refresh retry before failing.
 */
async function fetchWithAuthRetry(
  url: string,
  body: unknown,
  signal: AbortSignal,
): Promise<Response> {
  const makeRequest = (token: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

  const token = tokenService.getAccessToken();
  const res = await makeRequest(token);

  if (res.status === 401) {
    // Try refreshing the token once
    const newToken = await tryRefreshAccessToken();
    if (newToken) {
      return makeRequest(newToken);
    }
    // Refresh failed — redirect to copilot login
    tokenService.clearTokens();
    window.location.href = "/copilot/login";
    throw new Error("Session expired");
  }

  return res;
}

// ---------------------------------------------------------------------------
// Server conversation types
// ---------------------------------------------------------------------------

/** Shape of a message returned by GET /api/copilot/conversations/:id */
interface ServerMessage {
  role: string;
  content: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

/** Shape of GET /api/copilot/conversations/:id response */
interface ConversationDetailResponse {
  id: string;
  title: string;
  messages: ServerMessage[];
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

/** Shape of POST /api/copilot/chat response (buffered JSON) */
interface CopilotChatResponse {
  message: string;
  conversation_id: string;
  tool_calls?: Array<{
    tool_name: string;
    arguments: Record<string, unknown>;
    status: string;
    duration_ms?: number;
  }> | null;
  latency_ms?: number | null;
  tokens_used?: number | null;
  cost_usd?: number | null;
  log_id?: string | null;
  detected_account_uuid?: string | null;
}

/** Shape of POST /api/copilot/detect-account response */
interface DetectAccountResponse {
  detected_account_uuid?: string | null;
  intent?: string | null;
  company_name?: string | null;
  confidence?: number | null;
  latency_ms?: number | null;
}

/**
 * Convert server-side messages to ChatMessage[] for display.
 * Each server message gets a stable ID derived from its index and timestamp.
 */
function serverMessagesToChatMessages(serverMessages: ServerMessage[]): ChatMessage[] {
  return serverMessages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((msg, idx) => ({
      id: `history-${idx}-${msg.created_at ?? idx}`,
      role: msg.role as "user" | "assistant",
      content: msg.content,
      timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
      isStreaming: false,
    }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSSEChat(options: UseSSEChatOptions = {}): UseSSEChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => options.initialMessages ?? [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => {
    // Use provided initial value, fall back to localStorage
    if (options.initialConversationId !== undefined) {
      return options.initialConversationId ?? null;
    }
    return localStorage.getItem(CONVERSATION_ID_KEY);
  });
  const [error, setError] = useState<string | null>(null);

  // Abort controller for in-flight requests
  const abortRef = useRef<AbortController | null>(null);

  // Keep onResponseComplete ref fresh
  const onResponseCompleteRef = useRef(options.onResponseComplete);
  onResponseCompleteRef.current = options.onResponseComplete;

  // Keep a ref to messages so sendMessage doesn't need messages in its
  // dependency array (avoids stale-closure bugs and unnecessary re-creation).
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load a conversation from the server by ID, restoring messages into state
  const loadConversation = useCallback(async (convId: string) => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const token = tokenService.getAccessToken();
      const res = await fetch(`${API_BASE}/copilot/conversations/${convId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.status === 401) {
        // Try refreshing token
        const newToken = await tryRefreshAccessToken();
        if (newToken) {
          const retryRes = await fetch(`${API_BASE}/copilot/conversations/${convId}`, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          if (!retryRes.ok) {
            // Conversation not found or expired — start fresh
            localStorage.removeItem(CONVERSATION_ID_KEY);
            setConversationId(null);
            return;
          }
          const data = (await retryRes.json()) as ConversationDetailResponse;
          const restored = serverMessagesToChatMessages(data.messages);
          setMessages(restored);
          setConversationId(data.id);
          localStorage.setItem(CONVERSATION_ID_KEY, data.id);
          return;
        }
        // Refresh failed — clear and start fresh
        localStorage.removeItem(CONVERSATION_ID_KEY);
        setConversationId(null);
        return;
      }

      if (!res.ok) {
        // Conversation not found or expired — start fresh
        localStorage.removeItem(CONVERSATION_ID_KEY);
        setConversationId(null);
        return;
      }

      const data = (await res.json()) as ConversationDetailResponse;
      const restored = serverMessagesToChatMessages(data.messages);
      setMessages(restored);
      setConversationId(data.id);
      localStorage.setItem(CONVERSATION_ID_KEY, data.id);
    } catch {
      // Network error — start fresh silently
      localStorage.removeItem(CONVERSATION_ID_KEY);
      setConversationId(null);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  const sendMessage = useCallback(
    (text: string, source: "text" | "voice" = "text") => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      // Cancel any previous in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Add user message immediately
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: Date.now(),
        source,
      };

      // Create placeholder for assistant response (shows TypingDots)
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        isStreaming: true,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsLoading(true);
      setError(null);

      // Helper: insert QF card before assistant placeholder
      const insertQfCard = (accountUuid: string) => {
        copilotApi
          .get<QuickFactsData>(`/copilot/accounts/${accountUuid}/quick-facts`)
          .then((qfRes) => {
            setMessages((prev) => {
              // Only insert QF if not already present for this account
              const recentQf = prev.find(
                (m) =>
                  m.role === "quick_facts" &&
                  m.id === `qf-${accountUuid}`,
              );
              if (recentQf) return prev;

              // Insert QF card just before the assistant message
              const assistantIdx = prev.findIndex((m) => m.id === assistantId);
              if (assistantIdx === -1) return prev;

              const qfMsg: ChatMessage = {
                id: `qf-${accountUuid}`,
                role: "quick_facts",
                content: "",
                timestamp: Date.now(),
                quickFactsData: qfRes.data,
              };
              const updated = [...prev];
              updated.splice(assistantIdx, 0, qfMsg);
              return updated;
            });
          })
          .catch(() => {
            // QF fetch failed — not critical, just skip
          });
      };

      // Helper: extract last N user/assistant messages for conversation context.
      // Reads from messagesRef (not the closure) to avoid stale state.
      const getLastNMessages = (n: number): Array<{role: string; content: string}> => {
        const currentMessages = messagesRef.current
          .filter((m) => m.role === "user" || m.role === "assistant")
          .filter((m) => m.content && m.content.trim().length > 0)
          .slice(-n)
          .map((m) => ({ role: m.role, content: m.content }));
        return currentMessages;
      };

      // ── SEQUENTIAL-THEN-PARALLEL FLOW ──────────────────────────────
      // Step 1: detect-account FIRST (LLM intent classification, ~100-200ms)
      // Step 2: THEN in parallel: QF fetch (renders to screen) + chat LLM
      (async () => {
        try {
          // STEP 1: Detect account (sequential — must complete before chat)
          let detectedUuid: string | null = options.accountUuid ?? null;

          try {
            const detectRes = await fetchWithAuthRetry(
              `${API_BASE}/copilot/detect-account`,
              {
                message: trimmed,
                account_uuid: options.accountUuid ?? null,
                conversation_history: getLastNMessages(5),
                conversation_id: conversationId,
              },
              controller.signal,
            );

            if (detectRes.ok) {
              const detectData = (await detectRes.json()) as DetectAccountResponse;
              detectedUuid = detectData.detected_account_uuid ?? options.accountUuid ?? null;
            }
          } catch (detectErr) {
            if (detectErr instanceof DOMException && detectErr.name === "AbortError") {
              return; // User cancelled
            }
            // Detection failed — proceed without QF (no fallback)
            detectedUuid = null;
          }

          // STEP 2: Fire QF fetch + Chat in PARALLEL
          // 2a: QF fetch (async, renders card to screen immediately)
          if (detectedUuid) {
            insertQfCard(detectedUuid);
          }

          // 2b: Chat LLM request (with detected UUID so backend skips re-detection)
          const res = await fetchWithAuthRetry(
            `${API_BASE}/copilot/chat`,
            {
              message: trimmed,
              account_uuid: detectedUuid,
              conversation_id: conversationId,
            },
            controller.signal,
          );

          if (!res.ok) {
            // Parse backend detail if available, otherwise use a friendly fallback
            const isNonProd = window.location.hostname.includes("staging")
              || window.location.hostname === "localhost"
              || window.location.hostname === "127.0.0.1";
            let userMessage = "Something went wrong — please try again.";
            try {
              const errJson = await res.json();
              if (errJson.detail && typeof errJson.detail === "string") {
                userMessage = isNonProd
                  ? `Error ${res.status}: ${errJson.detail}`
                  : errJson.detail;
              } else if (isNonProd) {
                userMessage = `Error ${res.status}: ${JSON.stringify(errJson)}`;
              }
            } catch {
              // Not JSON — show status info on non-prod
              if (isNonProd) {
                userMessage = `Error ${res.status}: ${res.statusText}`;
              }
            }
            throw new Error(userMessage);
          }

          // Parse the complete JSON response
          const data = (await res.json()) as CopilotChatResponse;

          // Update conversation ID
          if (data.conversation_id) {
            setConversationId(data.conversation_id);
            localStorage.setItem(CONVERSATION_ID_KEY, data.conversation_id);
          }

          // Build tool calls summary for display
          const toolCallsSummary = data.tool_calls?.map((tc) => ({
            tool_name: tc.tool_name,
            status: tc.status,
            duration_ms: tc.duration_ms,
          })) ?? undefined;

          // Replace the placeholder with the full response
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: data.message,
                    isStreaming: false,
                    logId: data.log_id ?? undefined,
                    toolCalls: toolCallsSummary,
                  }
                : m
            )
          );

          // Fire response complete callback
          if (data.message) {
            onResponseCompleteRef.current?.(data.message);
          }
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === "AbortError") {
            // User cancelled — ignore
            return;
          }
          const msg =
            err instanceof Error ? err.message : "Connection failed";
          setError(msg);
          // Remove empty assistant placeholder on failure
          setMessages((prev) =>
            prev
              .map((m) =>
                m.id === assistantId ? { ...m, isStreaming: false } : m
              )
              .filter((m) => !(m.id === assistantId && !m.content))
          );
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [isLoading, conversationId, options.accountUuid]
  );

  const clearConversation = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setError(null);
    setIsLoading(false);
    // Clear persisted conversation ID
    localStorage.removeItem(CONVERSATION_ID_KEY);
  }, []);

  const markLastSpoken = useCallback(() => {
    setMessages((prev) => {
      // Find the last assistant message and mark it as spoken
      const lastIdx = [...prev].reverse().findIndex((m) => m.role === "assistant");
      if (lastIdx === -1) return prev;
      const idx = prev.length - 1 - lastIdx;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], spokenAloud: true };
      return updated;
    });
  }, []);

  const setMessageFeedback = useCallback(
    (messageId: string, feedback: number) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, feedbackState: feedback } : m
        )
      );
    },
    []
  );

  return {
    messages,
    isLoading,
    isLoadingHistory,
    conversationId,
    error,
    sendMessage,
    clearConversation,
    markLastSpoken,
    loadConversation,
    setMessageFeedback,
  };
}
